import { PATHS, SEARCH_PARAMS } from 'src/constants/navigateRoutes';
import {
  GOOGLE_CHAT_SPACE_NAME_REGEX,
  SNAKE_CASE_REGEX,
} from '../constants/Regex';
import {
  DEFAULT_URL_PROTOCOL_PREFIX,
  HTTP_PROTOCOL,
  HTTP_PROTOCOL_PREFIX,
  HTTPS_PROTOCOL,
  HTTPS_PROTOCOL_PREFIX,
  MIN_HOSTNAME_LENGTH,
  UNSAFE_URL_SUBSTRINGS,
  URL_PROTOCOL_SEPARATOR,
} from 'src/constants/url';
import * as Yup from 'yup';

export const validateTextInSnakeCase = (string: string) => {
  return SNAKE_CASE_REGEX.test(string);
};

export function validateGoogleChatSpaceName(spaceName: string) {
  return GOOGLE_CHAT_SPACE_NAME_REGEX.test(spaceName);
}

export function isValidJsonObjectString(value: string) {
  try {
    const parsedValue = JSON.parse(value);

    if (typeof parsedValue === 'object') {
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
}

export function getRedirectPathFromRedirectUrl(redirectUrl: string | null) {
  try {
    const parsedRedirectUrl = new URL(redirectUrl ?? '');

    return parsedRedirectUrl.pathname + parsedRedirectUrl.search;
  } catch (error) {
    return PATHS.home;
  }
}

export function getNavigatePathWithRedirectUrl(
  navigatePath: string,
  currentPath: string,
  encodedRedirectUrl: string,
  additionalSearchParams?: Record<string, string | null | undefined>,
) {
  const searchParams = new URLSearchParams();

  if (navigatePath !== currentPath) {
    searchParams.set(SEARCH_PARAMS.redirect, encodedRedirectUrl);
  }

  if (additionalSearchParams) {
    for (const [key, value] of Object.entries(additionalSearchParams)) {
      if (value) {
        searchParams.set(key, value);
      }
    }
  }

  const queryString = searchParams.toString();

  return queryString ? `${navigatePath}?${queryString}` : navigatePath;
}

export async function isValidEmail(email: string): Promise<boolean> {
  const schema = Yup.string().email('Invalid email format');

  try {
    await schema.validate(email);

    return true;
  } catch (error) {
    return false;
  }
}

export function isSafeHttpUrl(url: string): boolean {
  try {
    const trimmedUrl = url.trim();

    const normalizedUrl = trimmedUrl.toLowerCase();
    const containsUnsafeSubstring = UNSAFE_URL_SUBSTRINGS.some(
      (unsafeSubstring) => normalizedUrl.includes(unsafeSubstring),
    );

    if (containsUnsafeSubstring) {
      return false;
    }

    const parsedUrl = new URL(trimmedUrl);

    if (
      parsedUrl.protocol !== HTTP_PROTOCOL &&
      parsedUrl.protocol !== HTTPS_PROTOCOL
    ) {
      return false;
    }

    return parsedUrl.hostname.length >= MIN_HOSTNAME_LENGTH;
  } catch (error) {
    return false;
  }
}

export function isGoogleDriveUrl(url: string): boolean {
  try {
    const normalizedUrl = addMissingHttpProtocol(url);

    if (!isSafeHttpUrl(normalizedUrl)) {
      return false;
    }

    const parsedUrl = new URL(normalizedUrl);
    const hostname = parsedUrl.hostname.toLowerCase();

    return (
      hostname === 'drive.google.com' || hostname.endsWith('.drive.google.com')
    );
  } catch (error) {
    return false;
  }
}

export function addMissingHttpProtocol(url?: string | null): string {
  if (!url) {
    return '';
  }

  const trimmedUrl = url.trim();

  if (
    trimmedUrl.startsWith(HTTP_PROTOCOL_PREFIX) ||
    trimmedUrl.startsWith(HTTPS_PROTOCOL_PREFIX)
  ) {
    return trimmedUrl;
  }

  if (trimmedUrl.includes(URL_PROTOCOL_SEPARATOR)) {
    return trimmedUrl;
  }

  return `${DEFAULT_URL_PROTOCOL_PREFIX}${trimmedUrl}`;
}
